import { Component } from '@angular/core';
import { Type } from 'class-transformer';
import { FormulaEditorDialog } from '../../../../components/common/formulaEditor/formulaEditor.control';
import { ClassTransformerHelper } from '../../../../helpers/class.transformer.helper';
import { VariablesNodeInformation } from '../../../../models/basic/formulaEditor.model';
import { FormulaInputMode } from '../../../../models/enums/formulainputmode.enum';
import { WorkflowType } from '../../../../models/enums/workflowtype.enum';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../services/workflow.service';
import { DataCheck, FormulaWorkflowDialogContent } from '../../../../workflow/workflow.dialog';

/**
 * "Excel erzeugen" (excelErzeugenWFModule, 2026-10-21): free-form sheets from an object -- fixed
 * rows, one row per list entry with rows below it, conditions, series for a matrix. The result is a
 * file for "Save to media source" or "Send email" with attachment.
 * Server: MiddleShared/Workflow/DocumentOperations/ExcelErzeugen/ExcelErzeugenSettingsData.cs.
 */
@Component({
    selector: 'wf-excel-erzeugen-settings',
    templateUrl: './excel.erzeugen.settings.html',
    styles: [
        '.zeile { align-items: center; gap: 4px; flex-wrap: wrap; }',
        '.block { border-left: 2px solid rgba(128,128,128,.4); padding: 4px 0 4px 8px; margin: 4px 0; }',
        '.zelle { width: 200px; } .format { width: 90px; } .kurz { width: 70px; } .mittel { width: 160px; }',
        '.hinweis { opacity: .7; font-size: 12px; }',
    ]
})
export class ExcelErzeugenSettings extends FormulaWorkflowDialogContent {
    Data = new ExcelErzeugenSettingsData();
    Aktiv = 0;

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = 'excelErzeugenWFModule';
        reg.Caption = '@@Excel erzeugen';
        reg.GroupID = 'documentoperations';
        reg.Index = 50;
        reg.SettingsControl = ExcelErzeugenSettings;
        reg.SettingsTypeHelper = new ExcelErzeugenSettingsDataHelper();
        reg.WorkflowType = WorkflowType.Service;
        return reg;
    }

    initialize(data: any) {
        super.initialize(data);
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, ExcelErzeugenSettingsData);
        }
        if (!this.Data.Sheets?.length) {
            this.Data.Sheets = [new ExcelBlatt()];
        }
    }

    getResult(): any {
        return this.Data;
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (this.Data.Sheets.some(b => !b.Name?.trim())) {
            retVal.IsCorrect = false;
            retVal.Error = '@@Bitte jedem Blatt einen Namen geben';
        }
        return retVal;
    }

    get blatt(): ExcelBlatt {
        return this.Data.Sheets[this.Aktiv];
    }

    addBlatt() {
        const b = new ExcelBlatt();
        b.Name = 'Tabelle' + (this.Data.Sheets.length + 1);
        this.Data.Sheets.push(b);
        this.Aktiv = this.Data.Sheets.length - 1;
    }

    removeBlatt() {
        if (this.Data.Sheets.length > 1) {
            this.Data.Sheets.splice(this.Aktiv, 1);
            this.Aktiv = Math.max(0, this.Aktiv - 1);
        }
    }

    /** Column widths as "45, 14, 20". */
    breiten(b: ExcelBlatt): string {
        return (b.ColumnWidths ?? []).join(', ');
    }

    setBreiten(b: ExcelBlatt, text: string) {
        b.ColumnWidths = (text ?? '').split(/[;,\s]+/).filter(x => x).map(x => Number(x.replace(',', '.'))).filter(x => !isNaN(x));
    }

    addZeile(liste: ExcelZeile[]) {
        liste.push(new ExcelZeile());
    }

    removeZeile(liste: ExcelZeile[], i: number) {
        liste.splice(i, 1);
    }

    moveZeile(liste: ExcelZeile[], i: number, richtung: number) {
        const j = i + richtung;
        if (j >= 0 && j < liste.length) {
            const [z] = liste.splice(i, 1);
            liste.splice(j, 0, z);
        }
    }

    addZelle(z: ExcelZeile) {
        z.Cells.push('');
    }

    removeZelle(z: ExcelZeile, i: number) {
        z.Cells.splice(i, 1);
        if (z.Formats && i < z.Formats.length) {
            z.Formats.splice(i, 1);
        }
    }

    format(z: ExcelZeile, i: number): string {
        return z.Formats?.[i] ?? '';
    }

    setFormat(z: ExcelZeile, i: number, wert: string) {
        z.Formats = z.Formats ?? [];
        while (z.Formats.length <= i) {
            z.Formats.push(null);
        }
        z.Formats[i] = wert || null;
    }

    toggleSerie(z: ExcelZeile, an: boolean) {
        z.Series = an ? new ExcelSerie() : null;
    }

    trackIndex(i: number) {
        return i;
    }

    /** Formula editor; the series counter is a variable there. */
    showFormulaDialog(ziel: any, feld: string, zaehler?: string) {
        const variables = [...this.Variables];
        if (zaehler && !variables.some(x => x.Name === zaehler)) {
            const vni = new VariablesNodeInformation();
            vni.Name = zaehler;
            vni.AliasKey = zaehler;
            vni.VariableID = zaehler;
            variables.push(vni);
        }
        FormulaEditorDialog.ShowDialog({ Variables: variables, InputMode: FormulaInputMode.VariableName, Formula: ziel[feld] }, result => {
            if (result && result.Formula !== undefined) {
                ziel[feld] = result.Formula;
            }
            return true;
        });
    }
}

export class ExcelSerie {
    Variable = 'nr';
    From = '1';
    To = '';
    Cell = '';
}

export class ExcelZeile {
    Cells: string[] = [];
    List = '';
    Condition = '';
    Bold = false;
    Indent = 0;
    Formats: string[] = [];
    @Type(() => ExcelSerie)
    Series: ExcelSerie = null;
    @Type(() => ExcelZeile)
    Rows: ExcelZeile[] = [];
}

export class ExcelBlatt {
    Name = 'Tabelle1';
    ColumnWidths: number[] = [];
    @Type(() => ExcelZeile)
    Rows: ExcelZeile[] = [];
}

// @dynamic
export class ExcelErzeugenSettingsData {
    SourceKey = '';
    FileName = '';
    @Type(() => ExcelBlatt)
    Sheets: ExcelBlatt[] = [];
    ResultKey = '';
}

export class ExcelErzeugenSettingsDataHelper extends WorkflowModuleSettingsHelper {
    getExitPoints(settings: any): WorkflowExitInfo[] {
        return [new WorkflowExitInfo()];
    }

    getEmptySettingsInstance() {
        return new ExcelErzeugenSettingsData();
    }

    async fillActualState(module, state, wfData) {
        const settings = WorkflowModuleSettingsHelper.GetSettingsFromModule(module);
        if (state && settings?.ResultKey) {
            WorkflowModuleSettingsHelper.AddStatusKeysToState(state, [settings.ResultKey]);
        }
    }
}
